/* CATEGORY.md §3-2a — concept_compare_table (모바일 우선, v4 KB-only).
 * compare 위젯 = **specDiff 비교표만**. 답변(assistant_message)·Quick Take·개념별
 * 요약·상황(useCases)은 모두 위젯 밖 chat 버블이 담당한다(MCP 가 assistantMessage 에
 * 개념 섹션을 합쳐 전달). attribute(단독)는 위젯이 없다(버블만).
 *
 * 마크업/스타일은 퍼블 시안 ai-lists.html "제품 상세 → Category" 와 동일 구조:
 *   .ai-gp1-detail-container > .ai-gp1-scroll--container > .ai-gp1-scrollcontent-wrap
 *     > .ai-gp1-table--list > table.gp1-table  (+ 형제 .ai-gp1-custom--scrollbar)
 * 표준 gp1-table/pd-cell 스타일을 그대로 사용하고(별도 concept override 없음),
 * 가로스크롤은 .ai-gp1-scrollcontent-wrap(width:max-content) + 커스텀 드래그 스크롤바.
 * 헤더 Aspect 칸은 비우고, 행 라벨은 td > .pd-cell > <strong> (AISASSIT-145). */

import { useRef } from "react";
import { useLabel } from "../lib/labels";
import { useDragScrollbar } from "../lib/useDragScrollbar";

interface SpecDiffRow {
  label: string;
  values: Record<string, string>;
}

interface ConceptEntry {
  id: string;
  label: string;
}

interface ConceptData {
  mode?: "compare" | "attribute";
  concepts?: ConceptEntry[];
  specDiff?: SpecDiffRow[];
  conceptTable?: ConceptData;
  metadata?: { [key: string]: unknown };
}

interface Props {
  data: ConceptData;
  sessionToken: string;
}

function resolveData(data: ConceptData): ConceptData {
  if (data.conceptTable && Array.isArray(data.conceptTable.concepts)) {
    return data.conceptTable;
  }
  return data;
}

function SpecDiffSection({
  rows,
  concepts,
}: {
  rows: SpecDiffRow[];
  concepts: ConceptEntry[];
}) {
  // 가로스크롤 + 커스텀 드래그 스크롤바 (ai-lists.html 시안 구조와 동일).
  const scrollRef = useRef<HTMLDivElement>(null!);
  const barRef = useRef<HTMLDivElement>(null!);
  const trackRef = useRef<HTMLDivElement>(null!);
  const scrollbarRef = useRef<HTMLDivElement>(null!);
  useDragScrollbar(scrollRef, barRef, trackRef, scrollbarRef, [
    rows.length,
    concepts.length,
  ]);
  if (!rows.length) return null;
  return (
    <div className="ai-gp1-detail-container">
      <div
        ref={scrollRef}
        className="ai-gp1-scroll--container"
        style={{ overscrollBehaviorX: "contain", touchAction: "pan-x" }}
      >
        <div className="ai-gp1-scrollcontent-wrap">
          <div className="ai-gp1-table--list">
            <table className="gp1-table">
              <thead>
                <tr>
                  {/* Aspect 헤더 칸은 비움(시안) */}
                  <th scope="col" style={{ maxWidth: "120px" }}>
                    <span />
                  </th>
                  {concepts.map((c) => (
                    <th key={c.id} scope="col" style={{ minWidth: "180px" }}>
                      <span>{c.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <td>
                      <div className="pd-cell">
                        <strong>{r.label}</strong>
                      </div>
                    </td>
                    {concepts.map((c) => (
                      <td key={c.id}>
                        <div className="pd-cell">{r.values?.[c.id] || "—"}</div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div ref={scrollbarRef} className="ai-gp1-custom--scrollbar hidden">
        <div ref={trackRef} className="ai-gp1-scrollbar--track">
          <div ref={barRef} className="ai-gp1-scrollbar--bar" />
        </div>
      </div>
    </div>
  );
}

export default function ConceptCompareTableCard({ data }: Props) {
  const d = resolveData(data);
  const concepts = d.concepts || [];
  const rows = d.specDiff || [];
  const noDataLabel = useLabel("noData", "No concept data available.");

  if (!concepts.length || !rows.length) {
    return (
      <div className="ai-gp1--section">
        <p style={{ textAlign: "center", color: "#646464" }}>{noDataLabel}</p>
      </div>
    );
  }

  return (
    <div className="ai-gp1--section">
      <SpecDiffSection rows={rows} concepts={concepts} />
    </div>
  );
}
